/**
 * Uniform API-error model (design §13.3). Every RTK Query error in the app goes
 * through this one helper, which reads the NestJS `{statusCode, message, error}`
 * body and maps a status to the design's copy.
 *
 * Lives in `src/features/common/` (a plain module folder — no `routes.tsx`, no
 * `nav.ts`) because the shell `src/lib/` is off-limits to feature tasks.
 */

interface NestErrorBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
}

export interface NormalizedApiError {
  /** HTTP status, or `undefined` when the request never got an answer. */
  status?: number;
  /** Server `message` when present (already human-readable). */
  serverMessage?: string;
}

/**
 * `fetchBaseQuery` answers `{status: number | string, data}`; a thrown
 * `SerializedError` has `message` but no status. `status` numbers are kept as
 * numbers, string statuses are treated as transport failures.
 */
export function normalizeApiError(error: unknown): NormalizedApiError {
  if (error == null || typeof error !== 'object') {
    return { serverMessage: typeof error === 'string' ? error : undefined };
  }

  const candidate = error as {
    status?: number | string;
    error?: unknown;
    message?: unknown;
    data?: unknown;
  };

  const { status } = candidate;

  if (typeof status === 'number') {
    const body = (candidate.data ?? undefined) as NestErrorBody | undefined;
    const rawMessage = body?.message ?? candidate.message;
    const serverMessage = Array.isArray(rawMessage) ? rawMessage.join(' ') : rawMessage;
    return {
      status,
      serverMessage: typeof serverMessage === 'string' ? serverMessage : undefined,
    };
  }

  // A string status (`FETCH_ERROR`, `TIMEOUT_ERROR`, `PARSING_ERROR`) or a plain
  // thrown error: no usable answer from the server, so there is no status to map.
  const detail =
    typeof candidate.error === 'string'
      ? candidate.error
      : typeof candidate.message === 'string'
        ? candidate.message
        : undefined;

  return { serverMessage: detail };
}

/** Design §13.3: a 403 on a mutation always reads the same way. */
const PERMISSION_DENIED_MESSAGE = "You don't have permission to do that.";

/**
 * Toast copy for a failed mutation: the fixed phrase for a 403, otherwise the
 * caller's fallback plus the server detail when available (D5 — what is wrong
 * and how to fix it).
 */
export function mutationErrorMessage(error: unknown, fallback: string): string {
  const normalized = normalizeApiError(error);
  if (normalized.status === 403) {
    return PERMISSION_DENIED_MESSAGE;
  }
  if (normalized.serverMessage) {
    return `${fallback} ${normalized.serverMessage}`;
  }
  return fallback;
}
